import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { ISSUE_SEVERITY } from "contracts/enums/core";
import { CORE_WEB_VITALS_CONFIDENCE_LEVEL } from "contracts/enums/web-vitals";
import { JOURNEY_ROLE } from "../../enums/scan";
import { isOpen } from "../website/issueModel";
import { issueLinkMatchesPage } from "./issuePageResolution";
import { buildAllJourneyFrictions } from "./journeyFriction";
import {
  buildJourneyGraph,
  deriveRankedCandidatePathSkeletons,
  journeyPathSignature,
  selectLegacyPathSkeletons,
} from "./journeyPaths";
import { classifyJourneyRole, journeyRoleLabel } from "./journeyRoles";
import type { CandidateJourney, JourneyPreview } from "./journeyTypes";

export function buildJourneyPreview(data: OverviewResponse): JourneyPreview {
  const graph = buildJourneyGraph(data);
  const openIssues = data.issues.filter(isOpen);
  const issuesByPage = new Map(
    data.pages.map((page) => [
      page.id,
      openIssues.filter((issue) =>
        issue.pages.some((link) => issueLinkMatchesPage(link, page)),
      ),
    ]),
  );
  const issueCounts = new Map(
    [...issuesByPage].map(([pageId, issues]) => [pageId, issues.length]),
  );
  const importantPageIds = new Set(
    [...issuesByPage]
      .filter(([, issues]) =>
        issues.some(
          (issue) =>
            issue.severity === ISSUE_SEVERITY.CRITICAL ||
            issue.severity === CORE_WEB_VITALS_CONFIDENCE_LEVEL.HIGH,
        ),
      )
      .map(([pageId]) => pageId),
  );
  const roles = new Map(
    data.pages.map((page) => [
      page.id,
      classifyJourneyRole({
        page,
        inboundCount: graph.incoming.get(page.id)?.length ?? 0,
        outboundCount: graph.outgoing.get(page.id)?.length ?? 0,
      }),
    ]),
  );
  const pathInput = {
    data,
    graph,
    roles,
    issueCounts,
    importantPageIds,
  };
  const skeletons = deriveRankedCandidatePathSkeletons(pathInput);
  const legacySkeletons = selectLegacyPathSkeletons(skeletons);
  const legacyPathIds = new Map(
    legacySkeletons.map((skeleton, index) => [
      journeyPathSignature(skeleton),
      `path-${index + 1}-${skeleton.pageIds.join("-")}`,
    ]),
  );
  const paths = skeletons.map((skeleton, index) => {
    const steps = skeleton.pageIds.flatMap((pageId) => {
      const page = data.pages.find((item) => item.id === pageId);
      if (!page) return [];
      const role = roles.get(page.id) ?? JOURNEY_ROLE.UNCLASSIFIED;
      return [
        {
          page,
          role,
          roleLabel: journeyRoleLabel(role),
          issues: issuesByPage.get(page.id) ?? [],
          inboundCount: graph.incoming.get(page.id)?.length ?? 0,
          outboundCount: graph.outgoing.get(page.id)?.length ?? 0,
        },
      ];
    });
    const path: CandidateJourney = {
      id:
        legacyPathIds.get(journeyPathSignature(skeleton)) ??
        `path-route-${skeleton.pageIds.join("-")}`,
      rank: index,
      legacy: legacyPathIds.has(journeyPathSignature(skeleton)),
      steps,
      links: skeleton.links,
      concernCandidates: [],
      assessedPages: steps.filter((step) => step.page.contentFitness !== null)
        .length,
    };
    path.concernCandidates = buildAllJourneyFrictions(
      data,
      path,
      graph.outgoing,
    );
    return path;
  });
  return {
    paths,
    emptyReason: emptyReason(data, paths.length),
  };
}

function emptyReason(data: OverviewResponse, pathCount: number): string | null {
  if (data.pages.length === 0) return "No pages are available from this scan.";
  if (data.pages.length === 1) {
    return "No multi-page path can be derived from this scan.";
  }
  if (data.links.length === 0) {
    return "No multi-page path can be derived because no page links were recorded.";
  }
  return pathCount === 0
    ? "Faro found page relationships, but there is not enough evidence to organise them into a reliable structural path."
    : null;
}
